import { create } from "zustand";

const USER_STORAGE_KEY = "ftl_user";

export interface User {
  id: string;
  name: string;
  email: string;
  roles: string[];
  org_id: string;
  org_name: string;
  avatar?: string;
  is_platform_admin?: boolean;
}

/**
 * The name the app shows for a signed-in person, from /auth/me: their display
 * name, else the part of their email before the @, else "User".
 *
 * Row 9adbe359 (meeting c0efc3b6, 10-18): James saw "Good morning, there". The
 * sign-in paths in Login.tsx stored `name: ""` although /auth/me had just
 * returned the display name, so the home greeting fell back to "there" until a
 * page reload let AuthInitializer fill it in. Every path now names the person
 * the same way.
 */
export function displayNameOf(me: { display_name?: string | null; email?: string | null }, fallbackEmail = ""): string {
  const displayName = (me.display_name || "").trim();
  const email = (me.email || fallbackEmail || "").trim();
  return displayName || email.split("@")[0] || "User";
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User) => void;
  clearUser: () => void;
}

/** Hydrate user from localStorage on store creation. */
function loadPersistedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as User;
  } catch {
    // Corrupted data — clear it
    localStorage.removeItem(USER_STORAGE_KEY);
  }
  return null;
}

const initialUser = loadPersistedUser();

export const useAuthStore = create<AuthState>((set) => ({
  user: initialUser,
  isAuthenticated: initialUser !== null,
  setUser: (user: User) => {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    set({ user, isAuthenticated: true });
  },
  clearUser: () => {
    localStorage.removeItem(USER_STORAGE_KEY);
    set({ user: null, isAuthenticated: false });
  },
}));

/**
 * Check if the current user has (or implies) the given role.
 * ORG_ADMIN is treated as a super-admin that satisfies any role check.
 */
export function userHasRole(user: User | null, role: string): boolean {
  if (!user) return false;
  const roles = user.roles.map((r) => r.toUpperCase());
  return roles.includes("ORG_ADMIN") || roles.includes(role.toUpperCase());
}

/** A platform admin or an ORG_ADMIN: the people who see operator-only
 *  controls and diagnostics (same test FelixModelToggle applies). */
export function isOrgAdmin(user: User | null): boolean {
  return Boolean(
    user?.is_platform_admin ||
      user?.roles?.some((r) => r.toUpperCase() === "ORG_ADMIN"),
  );
}
